import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import BBBSeal from '../components/BBBSeal';
import { US_STATES } from '../data/states';
import { cartStorage, CartItem, checkoutFormStorage } from '../utils/cartStorage';
import { pushAddToCart, pushBeginCheckout } from '../utils/gtm';
import { useAutoSaveCart } from '../hooks/useAutoSaveCart';
import { Turnstile } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim() || '';

interface CheckoutState {
  planId?: string;
  fallDetection?: boolean;
  cartItems?: CartItem[];
}

interface PricingPlan {
  id: string;
  planType: string;
  name: string;
  monthlyPrice: number;
  annualTotal: number | null;
  billingCycle: string;
}

interface Product {
  id: string;
  name: string;
  shortName: string;
  description: string;
  imageFilename: string | null;
  features: string[];
}

interface PricingDetails {
  product: Product;
  plan: PricingPlan;
  pricing: {
    monthlyPrice: number;
    annualTotal: number | null;
    setupFee: number;
    activationFee: number;
    todayTotal: number;
    futureRenewal: {
      amount: number;
      date: string;
    };
  };
}

function getCheckoutApiUrl(): string {
  // Prefer shared config when REACT_APP_API_URL is set (strips trailing slashes).
  // Empty string → same-origin /api (Vercel rewrite to Railway).
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined') {
    const { hostname, port } = window.location;
    if (hostname === 'localhost' && port === '3000') return 'http://localhost:4000';
    return '';
  }
  return 'http://localhost:4000';
}

function getDeliveryDate(method: 'express' | 'overnight' | 'overnight-saturday'): string {
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();

  const addBusinessDays = (date: Date, n: number): Date => {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return d;
  };

  const getNextSaturday = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const daysUntilSat = day === 6 ? 7 : (6 - day + 7) % 7;
    if (daysUntilSat === 0) d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + daysUntilSat);
    return d;
  };

  let delivery: Date;
  if (method === 'express') {
    delivery = addBusinessDays(today, 2);
  } else if (method === 'overnight') {
    delivery = addBusinessDays(today, 1);
  } else {
    delivery = getNextSaturday(today);
  }

  const dayName = DAYS[delivery.getDay()];
  const m = delivery.getMonth() + 1;
  const d = delivery.getDate();
  return `Delivery ${dayName}, ${m}/${d}`;
}

// TEMPORARY: Set to false to require CVV again
const CVV_OPTIONAL = true;

const CheckoutPage: React.FC = () => {
  const API_URL = getCheckoutApiUrl();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as CheckoutState | null;
  const [cartSessionId, setCartSessionId] = useState<string | null>(null);
  const [abandonedCartId, setAbandonedCartId] = useState<string | null>(null);
  
  const [pricingData, setPricingData] = useState<PricingDetails | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [productImage, setProductImage] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Section completion tracking
  const [completedSections, setCompletedSections] = useState({
    memberInfo: false,
    protectDevice: false,
    shippingInfo: false,
    billingInfo: false
  });

  const [activeSection, setActiveSection] = useState<'memberInfo' | 'protectDevice' | 'shippingInfo' | 'billingInfo' | null>('memberInfo');

  // Form state
  const [memberInfo, setMemberInfo] = useState({
    fullName: '',
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    emailAddress: ''
  });

  const [protectionPlan, setProtectionPlan] = useState<'yes' | 'no' | null>(null);

  const [shippingInfo, setShippingInfo] = useState({
    shippingSame: true,
    shippingMethod: 'express', // 'express', 'overnight', 'overnight-saturday'
    fullName: '',
    streetAddress: '',
    apt: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const [billingInfo, setBillingInfo] = useState({
    cardNumber: '',
    expirationDate: '',
    cvv: '',
    nameOnCard: '',
    billingSame: true
  });

  const [promoCode, setPromoCode] = useState('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [diagnostics, setDiagnostics] = useState<{
    errors: string[];
    warnings: string[];
    info: string[];
  }>({
    errors: [],
    warnings: [],
    info: []
  });
  const [showDiagnostics, setShowDiagnostics] = useState(process.env.NODE_ENV === 'development');
  
  // Track previous product count to detect when new products are added
  const previousProductCountRef = useRef<number>(0);
  const protectionPlanPromptShownRef = useRef<Set<string>>(new Set()); // Track which product combinations we've already prompted for
  const productSignatureWhenCompletedRef = useRef<string | null>(null); // Product set when protect section was completed; reset section if cart product set changes
  const beginCheckoutFiredRef = useRef(false);
  const placeOrderBtnRef = useRef<HTMLButtonElement>(null);
  const memberInfoSectionRef = useRef<HTMLElement>(null);
  const protectDeviceSectionRef = useRef<HTMLElement>(null);
  const shippingInfoSectionRef = useRef<HTMLElement>(null);
  const billingInfoSectionRef = useRef<HTMLElement>(null);

  const requiresCompatibleSystemAccessory = (name?: string) => {
    const lower = name?.toLowerCase() || '';
    if (!lower) return false;
    return lower.includes('smoke detector') ||
      lower.includes('necklace with help button') ||
      lower.includes('pendant with emergency call button') ||
      lower.includes('fall detection pendant') ||
      lower.includes('fall detection alert pendant') ||
      lower.includes('mobile fall detection') ||
      lower.includes('medical alert bracelet') ||
      (lower.includes('emergency button alarm') && lower.includes('bracelet'));
  };

  const isLifeBeaconProduct = (item: CartItem) =>
    item.type === 'product' && (item.productShortName === 'lifebeacon' || item.productShortName === 'lifebeacon-fall');

  // Diagnostic logging utility
  const logDiagnostic = useCallback((type: 'error' | 'warning' | 'info', message: string) => {
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [Checkout] ${message}`;
    
    console.log(logMessage);
    
    setDiagnostics(prev => ({
      ...prev,
      [type === 'error' ? 'errors' : type === 'warning' ? 'warnings' : 'info']: [
        ...prev[type === 'error' ? 'errors' : type === 'warning' ? 'warnings' : 'info'],
        logMessage
      ]
    }));
  }, []);

  // Load form data from localStorage on mount
  useEffect(() => {
    const savedFormData = checkoutFormStorage.load();
    if (savedFormData) {
      if (savedFormData.memberInfo) setMemberInfo(savedFormData.memberInfo);
      if (savedFormData.protectionPlan !== undefined) setProtectionPlan(savedFormData.protectionPlan);
      if (savedFormData.shippingInfo) setShippingInfo(savedFormData.shippingInfo);
      if (savedFormData.billingInfo) setBillingInfo(savedFormData.billingInfo);
      if (savedFormData.completedSections) setCompletedSections(savedFormData.completedSections);
      if (savedFormData.activeSection) setActiveSection(savedFormData.activeSection);
    }
  }, []);

  // Validate checkout data
  const validateCheckoutData = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Validate member info
    if (!memberInfo.fullName?.trim()) errors.push('Full name is required');
    if (!memberInfo.streetAddress?.trim()) errors.push('Street address is required');
    if (!memberInfo.city?.trim()) errors.push('City is required');
    if (!memberInfo.state?.trim()) errors.push('State is required');
    if (!memberInfo.zipCode?.trim()) errors.push('Zip code is required');
    if (!memberInfo.phoneNumber?.trim()) errors.push('Phone number is required');
    if (!memberInfo.emailAddress?.trim()) errors.push('Email address is required');
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (memberInfo.emailAddress && !emailRegex.test(memberInfo.emailAddress)) {
      errors.push('Invalid email address format');
    }

    // Validate phone format (basic)
    const phoneRegex = /^[\d\s\-()]+$/;
    if (memberInfo.phoneNumber && !phoneRegex.test(memberInfo.phoneNumber.replace(/\s/g, ''))) {
      errors.push('Invalid phone number format');
    }

    // Validate shipping info if different
    if (!shippingInfo.shippingSame) {
      if (!shippingInfo.fullName?.trim()) errors.push('Shipping name is required');
      if (!shippingInfo.streetAddress?.trim()) errors.push('Shipping address is required');
      if (!shippingInfo.city?.trim()) errors.push('Shipping city is required');
      if (!shippingInfo.state?.trim()) errors.push('Shipping state is required');
      if (!shippingInfo.zipCode?.trim()) errors.push('Shipping zip code is required');
    }

    // Validate billing info
    if (!billingInfo.cardNumber?.trim()) errors.push('Card number is required');
    if (!billingInfo.expirationDate?.trim()) errors.push('Expiration date is required');
    if (!CVV_OPTIONAL && !billingInfo.cvv?.trim()) errors.push('CVV is required');
    if (!billingInfo.nameOnCard?.trim()) errors.push('Name on card is required');

    // Validate card number (basic - should be 13-19 digits)
    const cardNumberClean = billingInfo.cardNumber?.replace(/\s/g, '') || '';
    if (cardNumberClean && (cardNumberClean.length < 13 || cardNumberClean.length > 19)) {
      errors.push('Card number must be 13-19 digits');
    }

    // Validate expiration date format (MM/YY or MM/YYYY; browser autofill often supplies a 4-digit year)
    const expRegex = /^\d{2}\/(\d{2}|\d{4})$/;
    if (billingInfo.expirationDate && !expRegex.test(billingInfo.expirationDate)) {
      errors.push('Expiration date must be in MM/YY or MM/YYYY format');
    }

    // Validate CVV format (only when provided)
    if (billingInfo.cvv && !/^\d{3,4}$/.test(billingInfo.cvv)) {
      errors.push('CVV must be 3-4 digits');
    }

    // Validate cart
    if (cartItems.length === 0) {
      errors.push('Cart is empty');
    }

    // Validate pricing data if product in cart
    const hasProduct = cartItems.some(item => item.type === 'product');
    if (hasProduct && !pricingData) {
      errors.push('Pricing data is missing for product in cart');
    }

    return { valid: errors.length === 0, errors };
  };

  // Load cart items from route state or localStorage
  // Always merge with localStorage to ensure all items are present
  useEffect(() => {
    logDiagnostic('info', 'Initializing checkout page');
    
    // Always start with items from localStorage (source of truth)
    let items = cartStorage.getItems();
    logDiagnostic('info', `Cart items from localStorage: ${items.length} items`);
    console.log('[Checkout] Cart items from localStorage:', items);
    
    // If cartItems are passed via state, merge them with localStorage
    if (state?.cartItems && state.cartItems.length > 0) {
      logDiagnostic('info', `Merging ${state.cartItems.length} cart items from state`);
      console.log('[Checkout] Merging cart items from state:', state.cartItems);
      // Add any new items from state to localStorage
      state.cartItems.forEach(item => cartStorage.addItem(item));
      // Re-read to get all items (including ones that were already in localStorage)
      items = cartStorage.getItems();
      console.log('[Checkout] Cart items after merge:', items);
      logDiagnostic('info', `Cart items after merge: ${items.length} items`);
      setCartItems(items);
    } else if (state?.planId) {
      // Legacy support: if planId is passed, fetch pricing for that plan
      // Keep existing cart items from localStorage
      logDiagnostic('info', 'Legacy planId flow detected');
      console.log('[Checkout] Legacy planId flow, keeping cart items from localStorage');
      setCartItems(items);
    } else {
      // Use localStorage as source of truth
      logDiagnostic('info', 'Using localStorage as source of truth');
      console.log('[Checkout] Using localStorage as source of truth');
      setCartItems(items);
    }

    // Validate initial state
    if (items.length === 0) {
      logDiagnostic('warning', 'Cart is empty on checkout page load');
    }
    
    // Initialize product count ref
    const productCount = items.filter(item => item.type === 'product').length;
    previousProductCountRef.current = productCount;
  }, [state, logDiagnostic]);

  // Push GA4 begin_checkout when user lands on checkout (once per visit)
  useEffect(() => {
    if (beginCheckoutFiredRef.current || cartItems.length === 0) return;

    const productItems = cartItems.filter(item => item.type === 'product');
    const accessoryItems = cartItems.filter(item => item.type === 'accessory');
    const hasProduct = productItems.length > 0;

    if (hasProduct && !pricingData) return;

    const productTodayTotal = hasProduct && pricingData ? (pricingData.pricing?.todayTotal || 0) : 0;
    const accessoriesTotal = accessoryItems.reduce((sum, item) => sum + (item.price || 0), 0);
    const value = productTodayTotal + accessoriesTotal;

    const items: Array<{ item_id: string; item_name: string; price: number; quantity: number }> = [];
    const productPriceEach = productItems.length > 0 ? productTodayTotal / productItems.length : 0;
    productItems.forEach(item => {
      items.push({
        item_id: item.id,
        item_name: item.name,
        price: productPriceEach,
        quantity: 1,
      });
    });
    accessoryItems.forEach(item => {
      items.push({
        item_id: item.id,
        item_name: item.name,
        price: item.price || 0,
        quantity: 1,
      });
    });

    if (items.length > 0) {
      pushBeginCheckout({ currency: 'USD', value, items });
      beginCheckoutFiredRef.current = true;
    }
  }, [cartItems, pricingData]);

  // Helper function to add protection plan for a specific product
  const addProtectionPlanForProduct = useCallback(async (productCartItem: CartItem) => {
    if (!productCartItem.planId) {
      console.error('[Checkout] Product cart item missing planId');
      return;
    }

    // Fetch the plan details to get billing cycle
    try {
      const planResponse = await fetch(`${API_URL}/api/products/plans/${productCartItem.planId}`);
      if (!planResponse.ok) {
        throw new Error('Failed to fetch plan details');
      }
      const planData = await planResponse.json();
      const billingCycle = planData.plan?.billingCycle || 'monthly';
      
      // Set price based on billing cycle: $60 for annual, $5 for monthly
      const protectionPrice = billingCycle === 'annual' ? 60.00 : 5.00;

      // Check if protection plan already exists for this specific product
      const protectionPlanId = `accessory-accessory-1-${productCartItem.id}-${billingCycle}`;
      const currentCartItems = cartStorage.getItems();
      const existingProtectionPlan = currentCartItems.find(item => 
        item.id === protectionPlanId
      );

      if (existingProtectionPlan) {
        console.log('[Checkout] Protection plan already in cart for this product');
        return;
      }

      // Add protection plan to cart
      const protectionPlanItem: CartItem = {
        id: `accessory-accessory-1-${productCartItem.id}-${billingCycle}`,
        type: 'accessory',
        name: `Added Protection Plan (${productCartItem.name})`,
        accessoryId: 'accessory-1',
        price: protectionPrice,
        billingCycle: billingCycle
      };

      cartStorage.addItem(protectionPlanItem);
      pushAddToCart({
        currency: 'USD',
        value: protectionPrice,
        items: [{
          item_id: protectionPlanItem.id,
          item_name: protectionPlanItem.name,
          price: protectionPrice,
          quantity: 1,
          index: 0,
        }],
      });
      const updatedItems = cartStorage.getItems();
      console.log('[Checkout] Protection plan added to cart. Updated items:', updatedItems.length);
      console.log('[Checkout] Protection plan item:', protectionPlanItem);
      setCartItems([...updatedItems]);
      logDiagnostic('info', `Protection plan added to cart for ${productCartItem.name} (${billingCycle}): $${protectionPrice}`);

      // Fetch the image asynchronously and update if found (non-blocking)
      fetch(`${API_URL}/api/images`)
        .then(imagesResponse => imagesResponse.ok ? imagesResponse.json() : null)
        .then(imagesData => {
          if (imagesData) {
            const protectionImg = imagesData.images?.find((img: any) => 
              img.filename === 'alert1AddedProtectionPlan.jpg'
            );
            if (protectionImg) {
              const imageUrl = `${API_URL}/api/images/${protectionImg.id}`;
              const currentItems = cartStorage.getItems();
              const protectionPlanIndex = currentItems.findIndex(item => 
                item.id === protectionPlanItem.id
              );
              if (protectionPlanIndex >= 0 && !currentItems[protectionPlanIndex].imageUrl) {
                currentItems[protectionPlanIndex].imageUrl = imageUrl;
                try {
                  localStorage.setItem('alert1_cart_items', JSON.stringify(currentItems));
                  setCartItems([...currentItems]);
                } catch (error) {
                  console.error('[Checkout] Failed to update cart with image URL');
                }
              }
            }
          }
        })
        .catch(error => {
          console.log('[Checkout] Could not fetch protection plan image, using default');
        });
    } catch (error: any) {
      console.error('[Checkout] Error adding protection plan:', error);
      alert('Failed to add protection plan. Please try again.');
      setProtectionPlan(null);
    }
  }, [API_URL, setCartItems, setProtectionPlan, logDiagnostic]);

  // Auto-complete protection plan section if protection plan is in cart
  useEffect(() => {
    const protectionPlanInCart = cartItems.find(item => 
      item.type === 'accessory' && 
      item.name.toLowerCase().includes('protection plan')
    );
    
    if (protectionPlanInCart && !completedSections.protectDevice) {
      setCompletedSections(prev => ({ ...prev, protectDevice: true }));
      const productSignature = cartItems.filter(i => i.type === 'product').map(i => i.id).sort().join(',');
      productSignatureWhenCompletedRef.current = productSignature;
      logDiagnostic('info', 'Protection plan detected in cart, auto-completing section');
    }
  }, [cartItems, completedSections.protectDevice, logDiagnostic]);

  // Re-open Protect Your Device when: protection plan removed from cart, or product/plan set changed
  useEffect(() => {
    const hasProduct = cartItems.some(item => item.type === 'product');
    const protectionPlanInCart = cartItems.find(item =>
      item.type === 'accessory' && item.name.toLowerCase().includes('protection plan')
    );
    const productSignature = cartItems.filter(i => i.type === 'product').map(i => i.id).sort().join(',');

    if (!hasProduct) return;

    if (!protectionPlanInCart) {
      if (protectionPlan === 'no') {
        return;
      }
      if (completedSections.protectDevice || protectionPlan !== null) {
        setProtectionPlan(null);
        setCompletedSections(prev => ({ ...prev, protectDevice: false }));
        productSignatureWhenCompletedRef.current = null;
        protectionPlanPromptShownRef.current = new Set();
        setActiveSection('protectDevice');
        logDiagnostic('info', 'Protect-your-device section re-opened: no protection plan in cart');
      }
      return;
    }

    if (completedSections.protectDevice) {
      if (productSignatureWhenCompletedRef.current !== null && productSignatureWhenCompletedRef.current !== productSignature) {
        setProtectionPlan(null);
        setCompletedSections(prev => ({ ...prev, protectDevice: false }));
        productSignatureWhenCompletedRef.current = null;
        protectionPlanPromptShownRef.current = new Set();
        setActiveSection('protectDevice');
        logDiagnostic('info', 'Protect-your-device section re-opened: product/plan set changed');
      } else {
        productSignatureWhenCompletedRef.current = productSignature;
      }
    }
  }, [cartItems, completedSections.protectDevice, protectionPlan, logDiagnostic]);

  // Detect when a new product is added and prompt for protection plan assignment
  useEffect(() => {
    const productCartItems = cartItems.filter(item => item.type === 'product');
    const currentProductCount = productCartItems.length;
    const previousProductCount = previousProductCountRef.current;

    // Only trigger if:
    // 1. Product count increased (new product added)
    // 2. There are now 2+ products
    // 3. Protection plan section is already completed
    // 4. We haven't already prompted for this combination
    if (
      currentProductCount > previousProductCount &&
      currentProductCount >= 2 &&
      completedSections.protectDevice &&
      protectionPlan !== null
    ) {
      const productIds = productCartItems.map(p => p.id).sort().join(',');
      
      // Check if we've already prompted for this combination
      if (!protectionPlanPromptShownRef.current.has(productIds)) {
        protectionPlanPromptShownRef.current.add(productIds);
        
        // Get existing protection plans
        const existingProtectionPlans = cartItems.filter(item => 
          item.type === 'accessory' && 
          item.name.toLowerCase().includes('protection plan')
        );
        
        // Find products without protection
        const productsWithProtection = new Set<string>();
        existingProtectionPlans.forEach(pp => {
          // Extract product ID from protection plan ID (format: accessory-accessory-1-product-{productId}-{billingCycle})
          const match = pp.id.match(/product-([^-]+-[^-]+)/);
          if (match) {
            const productId = `product-${match[1]}`;
            productsWithProtection.add(productId);
          }
        });
        
        const productsWithoutProtection = productCartItems.filter(p => !productsWithProtection.has(p.id));
        
        if (productsWithoutProtection.length > 0) {
          // Prompt user to assign protection plans
          setTimeout(async () => {
            const productNames = productsWithoutProtection.map((p, idx) => `${idx + 1}. ${p.name}`).join('\n');
            const message = `You've added a new product to your cart. Which product would you like to add protection to?\n\n${productNames}\n\nEnter the number (1-${productsWithoutProtection.length}):`;
            const selection = prompt(message);
            
            if (selection) {
              const selectedIndex = parseInt(selection) - 1;
              if (selectedIndex >= 0 && selectedIndex < productsWithoutProtection.length) {
                await addProtectionPlanForProduct(productsWithoutProtection[selectedIndex]);
                
                // Ask if they want protection for remaining products
                const remainingProducts = productsWithoutProtection.filter((_, idx) => idx !== selectedIndex);
                if (remainingProducts.length > 0) {
                  const addMore = window.confirm(`Would you like to add protection to your other product${remainingProducts.length > 1 ? 's' : ''}?`);
                  if (addMore) {
                    for (const product of remainingProducts) {
                      const addThis = window.confirm(`Add protection to ${product.name}?`);
                      if (addThis) {
                        await addProtectionPlanForProduct(product);
                      }
                    }
                  }
                }
              }
            }
          }, 500); // Small delay to ensure UI is updated
        }
      }
    }

    // Update the ref for next comparison
    previousProductCountRef.current = currentProductCount;
  }, [cartItems, completedSections.protectDevice, protectionPlan, addProtectionPlanForProduct]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    checkoutFormStorage.save({
      memberInfo,
      protectionPlan,
      shippingInfo,
      billingInfo,
      completedSections,
      activeSection
    });
  }, [memberInfo, protectionPlan, shippingInfo, billingInfo, completedSections, activeSection]);

  // Save cart progress to database
  const saveCartProgress = async (currentSectionName?: string) => {
    try {
      const productCartItem = cartItems.find(item => item.type === 'product');
      const productInfo = productCartItem ? {
        productId: productCartItem.productId,
        productShortName: productCartItem.productShortName,
        productName: productCartItem.name,
        pricingPlanId: productCartItem.planId,
        planName: productCartItem.planName,
        billingCycle: pricingData?.plan?.billingCycle,
      } : null;

      // Determine current section if not provided
      const sectionName = currentSectionName || activeSection || 'unknown';

      const response = await fetch(`${API_URL}/api/abandoned-carts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: cartSessionId,
          cartItems,
          memberInfo,
          protectionPlan: protectionPlanInCart ? 'yes' : protectionPlan,
          shippingInfo,
          billingInfo: {
            ...billingInfo,
            // For abandoned carts, we can store full card number (will be encrypted on backend)
            // Only mask for logging/debugging
            cardNumber: billingInfo.cardNumber || '',
          },
          completedSections,
          currentSection: sectionName,
          pricingData,
          productInfo,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.sessionId && !cartSessionId) {
          setCartSessionId(result.sessionId);
        }
        if (result.cartId) {
          setAbandonedCartId(result.cartId);
        }
        logDiagnostic('info', `Cart progress saved: ${sectionName}`);
      } else {
        logDiagnostic('warning', `Failed to save cart progress: ${response.status}`);
      }
    } catch (error: any) {
      logDiagnostic('warning', `Error saving cart progress: ${error.message}`);
      // Don't block user flow if save fails
    }
  };

  // Auto-save hook - saves automatically with debouncing
  const { debouncedSave, immediateSave } = useAutoSaveCart({
    saveFunction: () => saveCartProgress(),
    debounceMs: 2000, // 2 seconds
    enabled: true,
  });

  // Auto-save when form fields change (debounced)
  const prevFormDataRef = useRef({ memberInfo, shippingInfo, billingInfo, protectionPlan });
  useEffect(() => {
    const prev = prevFormDataRef.current;
    const hasChanged = 
      JSON.stringify(prev.memberInfo) !== JSON.stringify(memberInfo) ||
      JSON.stringify(prev.shippingInfo) !== JSON.stringify(shippingInfo) ||
      JSON.stringify(prev.billingInfo) !== JSON.stringify(billingInfo) ||
      prev.protectionPlan !== protectionPlan;

    if (hasChanged) {
      debouncedSave();
      prevFormDataRef.current = { memberInfo, shippingInfo, billingInfo, protectionPlan };
    }
  }, [memberInfo, shippingInfo, billingInfo, protectionPlan, debouncedSave]);

  // Auto-save when cart items change
  const prevCartItemsRef = useRef(cartItems);
  useEffect(() => {
    if (JSON.stringify(prevCartItemsRef.current) !== JSON.stringify(cartItems)) {
      immediateSave(); // Save immediately on cart changes
      prevCartItemsRef.current = cartItems;
    }
  }, [cartItems, immediateSave]);

  // Handler functions for section completion
  const handleSaveMemberInfo = () => {
    // Validate member info
    if (memberInfo.fullName && memberInfo.streetAddress && memberInfo.city && 
        memberInfo.state && memberInfo.zipCode && memberInfo.phoneNumber && memberInfo.emailAddress) {
      setCompletedSections(prev => ({ ...prev, memberInfo: true }));
      setActiveSection('protectDevice');
      // Save progress
      saveCartProgress('protectDevice');
      setTimeout(() => protectDeviceSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } else {
      alert('Please fill in all required fields');
    }
  };

  const handleSaveProtectionPlan = () => {
    if (protectionPlanInCart) {
      // If protection plan is in cart, just mark as completed
      setCompletedSections(prev => ({ ...prev, protectDevice: true }));
      const productSignature = cartItems.filter(i => i.type === 'product').map(i => i.id).sort().join(',');
      productSignatureWhenCompletedRef.current = productSignature;
      setActiveSection('shippingInfo');
      // Save progress
      saveCartProgress('shippingInfo');
      setTimeout(() => shippingInfoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      return;
    }
    
    if (protectionPlan !== null) {
      setCompletedSections(prev => ({ ...prev, protectDevice: true }));
      const productSignature = cartItems.filter(i => i.type === 'product').map(i => i.id).sort().join(',');
      productSignatureWhenCompletedRef.current = productSignature;
      setActiveSection('shippingInfo');
      // Save progress
      saveCartProgress('shippingInfo');
      setTimeout(() => shippingInfoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } else {
      alert('Please select a protection plan option');
    }
  };

  const handleRemoveProtectionPlan = () => {
    if (!protectionPlanInCart) return;
    
    const protectionPlanIndex = cartItems.findIndex(item => 
      item.type === 'accessory' && 
      item.name.toLowerCase().includes('protection plan')
    );
    
    if (protectionPlanIndex >= 0) {
      cartStorage.removeItem(protectionPlanIndex);
      const updatedItems = cartStorage.getItems();
      setCartItems(updatedItems);
      logDiagnostic('info', 'Protection plan removed from cart');
      
      // Reset protection plan selection
      setProtectionPlan(null);
      setCompletedSections(prev => ({ ...prev, protectDevice: false }));
    }
  };

  const handleProtectionPlanChange = async (value: 'yes' | 'no') => {
    console.log('[Checkout] Protection plan change:', value);
    setProtectionPlan(value);

    // Get all products in cart
    const productCartItems = cartItems.filter(item => item.type === 'product');

    if (value === 'yes') {
      // Check if protection plan already exists in cart
      const existingProtectionPlans = cartItems.filter(item => 
        item.type === 'accessory' && 
        item.name.toLowerCase().includes('protection plan')
      );

      // If there are multiple products, ask which one to add protection to
      if (productCartItems.length > 1) {
        // Check how many products already have protection
        const productsWithProtection = new Set<string>();
        existingProtectionPlans.forEach(pp => {
          // Try to match protection plan to product by billing cycle
          const matchingProduct = productCartItems.find(p => {
            // We need to fetch the plan to get billing cycle
            return true; // We'll handle this in the prompt
          });
          if (matchingProduct) {
            productsWithProtection.add(matchingProduct.id);
          }
        });

        // Find products without protection
        const productsWithoutProtection = productCartItems.filter(p => !productsWithProtection.has(p.id));
        
        if (productsWithoutProtection.length === 0) {
          alert('All products in your cart already have protection plans.');
          setProtectionPlan(null);
        return;
      }

        // Ask which product to add protection to
        if (productsWithoutProtection.length === 1) {
          // Only one product without protection, add it automatically
          await addProtectionPlanForProduct(productsWithoutProtection[0]);
        } else {
          // Multiple products without protection - ask user
          const productNames = productsWithoutProtection.map((p, idx) => `${idx + 1}. ${p.name}`).join('\n');
          const selection = prompt(`Which product would you like to add protection to?\n\n${productNames}\n\nEnter the number (1-${productsWithoutProtection.length}):`);
          
          if (!selection) {
            setProtectionPlan(null);
            return;
          }

          const selectedIndex = parseInt(selection) - 1;
          if (selectedIndex >= 0 && selectedIndex < productsWithoutProtection.length) {
            await addProtectionPlanForProduct(productsWithoutProtection[selectedIndex]);
            
            // Ask if they want protection for the remaining products
            const remainingProducts = productsWithoutProtection.filter((_, idx) => idx !== selectedIndex);
            if (remainingProducts.length > 0) {
              const addMore = window.confirm(`Would you like to add protection to your other product${remainingProducts.length > 1 ? 's' : ''}?`);
              if (addMore) {
                for (const product of remainingProducts) {
                  const addThis = window.confirm(`Add protection to ${product.name}?`);
                  if (addThis) {
                    await addProtectionPlanForProduct(product);
                  }
                }
              }
            }
          } else {
            alert('Invalid selection. Please try again.');
            setProtectionPlan(null);
            return;
          }
        }
      } else if (productCartItems.length === 1) {
        // Single product - add protection plan
        await addProtectionPlanForProduct(productCartItems[0]);
      } else {
        alert('No products in cart. Please add a product first.');
        setProtectionPlan(null);
        return;
      }
    } else if (value === 'no') {
      // Remove all protection plans from cart
      const protectionPlanIndices: number[] = [];
      cartItems.forEach((item, index) => {
        if (item.type === 'accessory' && item.name.toLowerCase().includes('protection plan')) {
          protectionPlanIndices.push(index);
        }
      });
      
      // Remove in reverse order to maintain correct indices
      protectionPlanIndices.reverse().forEach(index => {
        cartStorage.removeItem(index);
      });
      
        const updatedItems = cartStorage.getItems();
      console.log('[Checkout] Protection plan(s) removed. Updated items:', updatedItems.length);
      setCartItems([...updatedItems]);
      logDiagnostic('info', 'Protection plan(s) removed from cart');
    }
  };

  const handleSaveShippingInfo = () => {
    if (shippingInfo.shippingSame || (shippingInfo.fullName && shippingInfo.streetAddress && 
        shippingInfo.city && shippingInfo.state && shippingInfo.zipCode)) {
      setCompletedSections(prev => ({ ...prev, shippingInfo: true }));
      setActiveSection('billingInfo');
      // Save progress
      saveCartProgress('billingInfo');
      setTimeout(() => billingInfoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    } else {
      alert('Please fill in all required shipping fields');
    }
  };

  const handleSaveBillingInfo = () => {
    const billingComplete = billingInfo.cardNumber && billingInfo.expirationDate && billingInfo.nameOnCard &&
      (CVV_OPTIONAL || billingInfo.cvv);
    if (billingComplete) {
      setCompletedSections(prev => ({ ...prev, billingInfo: true }));
      setActiveSection(null);
      // Save progress
      saveCartProgress('review');
      // Scroll to Place Order button after section collapses
      setTimeout(() => {
        placeOrderBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    } else {
      alert('Please fill in all required billing fields');
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handlePlaceOrder = async () => {
    logDiagnostic('info', 'Place order button clicked');
    
    const hasProductInCart = cartItems.some(item => item.type === 'product');
    const requiresSystemAccessoryInCart = cartItems.some(item => item.type === 'accessory' && requiresCompatibleSystemAccessory(item.name));
    if (!hasProductInCart && requiresSystemAccessoryInCart) {
      const confirmed = window.confirm('Accessories like the Smoke Detector or pendant activators require an active Alert1 medical alert system and plan. Please confirm you already have a compatible system before completing your order.');
      if (!confirmed) {
        logDiagnostic('warning', 'Customer declined system confirmation at checkout');
        alert('Please add a compatible Alert1 system and plan before completing your order.');
        navigate('/pricing#product-tiles');
        return;
      }
    }
    
    // Validate all required fields
    if (!completedSections.memberInfo || !completedSections.protectDevice || 
        !completedSections.shippingInfo || !completedSections.billingInfo) {
      const missingSections = [];
      if (!completedSections.memberInfo) missingSections.push('Member Info');
      if (!completedSections.protectDevice) missingSections.push('Protect Device');
      if (!completedSections.shippingInfo) missingSections.push('Shipping Info');
      if (!completedSections.billingInfo) missingSections.push('Billing Info');
      
      logDiagnostic('warning', `Missing sections: ${missingSections.join(', ')}`);
      alert('Please complete all sections before placing your order');
      return;
    }

    if (cartItems.length === 0) {
      logDiagnostic('error', 'Attempted to place order with empty cart');
      alert('Your cart is empty. Please add items to continue.');
      return;
    }

    const lifeBeaconWithoutStyle = cartItems.find(item => isLifeBeaconProduct(item) && !item.deviceStyle);
    if (lifeBeaconWithoutStyle) {
      logDiagnostic('warning', 'LifeBeacon in cart without Pendant/Watch selection');
      alert('Please select Pendant or Watch for your LifeBeacon device in the order summary above.');
      return;
    }

    // Run validation
    const validation = validateCheckoutData();
    if (!validation.valid) {
      logDiagnostic('error', `Validation failed: ${validation.errors.join('; ')}`);
      alert(`Please fix the following errors:\n${validation.errors.join('\n')}`);
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      alert('Please complete the security check above, then try again.');
      return;
    }

    setIsSubmitting(true);
    logDiagnostic('info', 'Starting order submission');

    try {
      // Prepare order data
      const orderData = {
        customer: {
          fullName: memberInfo.fullName,
          streetAddress: memberInfo.streetAddress,
          apt: memberInfo.apt,
          city: memberInfo.city,
          state: memberInfo.state,
          zipCode: memberInfo.zipCode,
          phoneNumber: memberInfo.phoneNumber,
          emailAddress: memberInfo.emailAddress,
        },
        cartItems,
        pricingData,
        shippingInfo: {
          ...shippingInfo,
          shippingSame: shippingInfo.shippingSame,
          shippingMethod: shippingInfo.shippingMethod,
        },
        billingInfo: {
          ...billingInfo,
          billingSame: billingInfo.billingSame,
        },
        protectionPlan: protectionPlanInCart ? 'yes' : protectionPlan,
        promoCode: promoCode || null,
        ...(TURNSTILE_SITE_KEY && turnstileToken ? { turnstileToken } : {}),
      };

      logDiagnostic('info', `Submitting order with ${cartItems.length} items`);
      logDiagnostic('info', `API URL: ${API_URL}/api/orders`);
      console.log('[Checkout] Submitting order:', {
        ...orderData,
        billingInfo: {
          ...orderData.billingInfo,
          cardNumber: orderData.billingInfo.cardNumber.replace(/\d(?=\d{4})/g, '*'), // Mask card number
          cvv: '***'
        }
      });

      const startTime = Date.now();
      let response: Response;
      
      try {
        response = await fetch(`${API_URL}/api/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(orderData),
        });
      } catch (fetchError: any) {
        // Network error or fetch failed
        logDiagnostic('error', `Network error: ${fetchError.message}`);
        logDiagnostic('error', `Failed to reach API at ${API_URL}/api/orders`);
        throw new Error(`Network error: Unable to connect to server. ${fetchError.message}`);
      }

      const responseTime = Date.now() - startTime;
      logDiagnostic('info', `API response received in ${responseTime}ms (status: ${response.status})`);

      if (!response.ok) {
        let errorData;
        const responseText = await response.text();
        logDiagnostic('error', `API error ${response.status} response body: ${responseText}`);
        
        try {
          errorData = JSON.parse(responseText);
        } catch (parseError) {
          // If JSON parsing fails, use the raw text
          logDiagnostic('error', `Failed to parse error response as JSON: ${parseError}`);
          throw new Error(`Server error (${response.status}): ${responseText || 'Unknown error'}`);
        }
        
        logDiagnostic('error', `API error ${response.status}: ${JSON.stringify(errorData, null, 2)}`);
        const errorMessage = errorData.message || errorData.error || `Server error (${response.status})`;
        const errorDetails = errorData.details ? ` Details: ${errorData.details}` : '';
        throw new Error(`${errorMessage}${errorDetails}`);
      }

      const result = await response.json();
      console.log('[Checkout] Order diagnostics:', result.diagnostics ?? null);
      if (result.diagnostics && result.diagnostics.emailSent === false) {
        console.warn(
          '[Checkout] Confirmation email was not sent:',
          result.diagnostics.emailError ?? 'unknown',
          result.diagnostics.emailMessageId
            ? `(messageId: ${result.diagnostics.emailMessageId})`
            : ''
        );
      }
      logDiagnostic('info', `Order placed successfully: ${result.order.orderId}`);
      logDiagnostic('info', `Confirmation: ${result.order.confirmationNumber}`);
      if (result.diagnostics && typeof result.diagnostics.emailSent === 'boolean') {
        logDiagnostic(
          result.diagnostics.emailSent ? 'info' : 'warning',
          `Confirmation email: ${result.diagnostics.emailSent ? 'sent' : 'not sent'}${result.diagnostics.emailError ? ` — ${result.diagnostics.emailError}` : ''}`
        );
      }
      if (result.order.externalOrderId) {
        logDiagnostic('info', `External Order ID: ${result.order.externalOrderId}`);
      }
      if (result.order.orderStatus) {
        logDiagnostic('info', `Order status: ${result.order.orderStatus}`);
      }
      if (result.externalApiResponse?.orderStatus) {
        logDiagnostic('info', `External API status: ${result.externalApiResponse.orderStatus}`);
      }
      console.log('[Checkout] Order placed successfully:', result);

      // Mark abandoned cart as completed if we have the cart ID (from save response; no GET needed)
      if (abandonedCartId) {
        try {
          const completeResponse = await fetch(`${API_URL}/api/abandoned-carts/${abandonedCartId}/complete`, {
            method: 'POST',
          });
          if (completeResponse.ok) {
            logDiagnostic('info', 'Abandoned cart marked as completed');
          }
        } catch (error) {
          // Don't block order completion if this fails
          logDiagnostic('warning', 'Failed to mark cart as completed');
        }
      }

      // Clear cart and form data
      cartStorage.clear();
      checkoutFormStorage.clear();
      logDiagnostic('info', 'Cart and form data cleared');

      const orderTotal = Number(orderTotalDisplay.toFixed(2));
      const subtotal = productTodayTotal + accessoriesTotal;
      const emailDiagnostics =
        result.diagnostics && typeof result.diagnostics.emailSent === 'boolean'
          ? {
              emailSent: result.diagnostics.emailSent as boolean,
              emailError: (result.diagnostics.emailError as string | null) ?? null,
              emailMessageId: (result.diagnostics.emailMessageId as string | null) ?? null,
            }
          : undefined;

      navigate('/order-success', {
        replace: true,
        state: {
          orderNumber: result.order.confirmationNumber || result.order.orderId,
          orderId: result.order.orderId,
          orderStatus: result.order.orderStatus,
          externalOrderId: result.order.externalOrderId || null,
          externalOrderStatus: result.externalApiResponse?.orderStatus || null,
          externalApiResponse: result.externalApiResponse || null,
          total: orderTotal,
          subtotal,
          email: memberInfo.emailAddress,
          product: product?.name || plan?.name || 'Alert1 Medical Alert System',
          offerCode: promoCode || undefined,
          placedAt: new Date().toISOString(),
          emailDiagnostics,
        }
      });
    } catch (error: any) {
      const errorMessage = error.message || 'Unknown error occurred';
      logDiagnostic('error', `Order submission failed: ${errorMessage}`);
      console.error('[Checkout] Error placing order:', error);
      console.error('[Checkout] Error stack:', error.stack);
      console.error('[Checkout] Full error object:', error);
      
      // Show more detailed error message
      let userMessage = errorMessage;
      if (errorMessage.includes('NetworkError') || errorMessage.includes('Failed to fetch')) {
        userMessage = 'Network error: Unable to connect to server. Please check your internet connection and try again.';
      } else if (errorMessage.includes('404')) {
        userMessage = 'Order endpoint not found. Please contact support.';
      } else if (errorMessage.includes('500')) {
        userMessage = 'Server error occurred. Please try again or contact support if the problem persists.';
      }
      
      alert(`Failed to place order: ${userMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSection = (section: 'memberInfo' | 'protectDevice' | 'shippingInfo' | 'billingInfo') => {
    setActiveSection(section);
  };

  // Fetch pricing data based on selected product and plan
  useEffect(() => {
    // If cart items are present, use the first product item
    // For accessories, we'll calculate pricing directly from cart item
    const productCartItem = cartItems.find(item => item.type === 'product');
    
    if (state?.planId) {
      // Legacy support: fetch pricing for planId and create cart item
      console.log('[Checkout] Fetching pricing for plan:', state.planId);
      fetch(`${API_URL}/api/products/plans/${state.planId}`)
        .then(res => res.json())
        .then(data => {
          console.log('[Checkout] Received pricing data:', data);
          setPricingData(data);
          
          // Create cart item from pricing data if not already in cart
          if (data.product && data.plan) {
            const existingProduct = cartItems.find(item => 
              item.type === 'product' && 
              item.productId === data.product.id && 
              item.planId === data.plan.id
            );
            
            if (!existingProduct) {
              // Fetch product image
              fetch(`${API_URL}/api/images`)
                .then(imagesRes => imagesRes.json())
                .then(imagesData => {
                  const productImg = imagesData.images?.find((img: any) => 
                    img.filename.toLowerCase() === data.product.imageFilename?.toLowerCase()
                  );
                  const imageUrl = productImg ? `${API_URL}/api/images/${productImg.id}` : '';
                  
                  const cartItem: CartItem = {
                    id: `product-${data.product.id}-${data.plan.id}`,
                    type: 'product',
                    name: data.product.name,
                    imageUrl: imageUrl,
                    planId: data.plan.id,
                    planName: data.plan.name,
                    productId: data.product.id,
                    productShortName: data.product.shortName,
                    fallDetection: state?.fallDetection || undefined
                  };
                  
                  cartStorage.addItem(cartItem);
                  const updatedItems = cartStorage.getItems();
                  console.log('[Checkout] Created cart item from legacy planId flow:', cartItem);
                  setCartItems(updatedItems);
                })
                .catch(() => {
                  // If image fetch fails, still create cart item without image
                  const cartItem: CartItem = {
                    id: `product-${data.product.id}-${data.plan.id}`,
                    type: 'product',
                    name: data.product.name,
                    planId: data.plan.id,
                    planName: data.plan.name,
                    productId: data.product.id,
                    productShortName: data.product.shortName,
                    fallDetection: state?.fallDetection || undefined
                  };
                  
                  cartStorage.addItem(cartItem);
                  const updatedItems = cartStorage.getItems();
                  console.log('[Checkout] Created cart item from legacy planId flow (no image):', cartItem);
                  setCartItems(updatedItems);
                });
            }
          }
          
          setLoading(false);
        })
        .catch(error => {
          console.error('[Checkout] Error fetching pricing data:', error);
          setLoading(false);
        });
    } else if (productCartItem?.planId) {
      // Fetch pricing for product from cart
      console.log('[Checkout] Fetching pricing for plan from cart:', productCartItem.planId);
      fetch(`${API_URL}/api/products/plans/${productCartItem.planId}`)
        .then(res => res.json())
        .then(data => {
          console.log('[Checkout] Received pricing data from cart:', data);
          setPricingData(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('[Checkout] Error fetching pricing data:', error);
          setLoading(false);
        });
    } else {
      // No product plan to fetch - may be accessories only or empty cart
      setLoading(false);
    }
  }, [state, cartItems, API_URL]);

  // Fetch logo and product images
  useEffect(() => {
    fetch(`${API_URL}/api/images`)
      .then(res => res.json())
      .then(data => {
        if (data.images && data.images.length > 0) {
          // Find logo
          const logoFile = data.images.find((img: any) => 
            img.filename.toLowerCase().includes('logo') && 
            img.filename.toLowerCase().includes('.webp')
          );
          if (logoFile) {
            setLogoImage(`${API_URL}/api/images/${logoFile.id}`);
          }

          // Find product image if pricing data is available
          if (pricingData?.product.imageFilename) {
            const img = data.images.find((i: any) => 
              i.filename.toLowerCase() === pricingData.product.imageFilename?.toLowerCase()
            );
            if (img) {
              setProductImage(`${API_URL}/api/images/${img.id}`);
            }
          }
        }
      })
      .catch(error => console.error('Error fetching images:', error));
  }, [pricingData, API_URL]);

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="loading-container" style={{ padding: '4rem', textAlign: 'center' }}>
          <p>Loading checkout...</p>
        </div>
      </div>
    );
  }

  // If cart has items but no pricing data (accessories-only cart), allow checkout to proceed
  const hasCartItems = cartItems.length > 0;
  const hasProduct = cartItems.some(item => item.type === 'product');
  const requiresSystemAccessoryInCart = cartItems.some(item => item.type === 'accessory' && requiresCompatibleSystemAccessory(item.name));
  const showSystemWarning = hasCartItems && !hasProduct && requiresSystemAccessoryInCart;
  
  // Only require pricing data if there's a product in the cart
  if (!pricingData && hasProduct) {
    return (
      <div className="checkout-page">
        <div className="error-container" style={{ padding: '4rem', textAlign: 'center' }}>
          <h2>Unable to load pricing information</h2>
          <p>Please return to the cart and try again.</p>
          <a href="/cart" style={{ color: '#d32f2f', fontWeight: 'bold' }}>Return to Cart</a>
        </div>
      </div>
    );
  }

  // Calculate totals from cart items
  const productItems = cartItems.filter(item => item.type === 'product');
  const accessoryItems = cartItems.filter(item => item.type === 'accessory');
  
  console.log('[Checkout] Cart items for totals:', {
    totalItems: cartItems.length,
    productItems: productItems.length,
    accessoryItems: accessoryItems.length,
    accessoryNames: accessoryItems.map(a => a.name),
  });
  
  const protectionPlanInCart = cartItems.find(item => 
    item.type === 'accessory' && 
    item.name.toLowerCase().includes('protection plan')
  );
  
  console.log('[Checkout] Protection plan in cart:', protectionPlanInCart ? {
    name: protectionPlanInCart.name,
    price: protectionPlanInCart.price,
    billingCycle: protectionPlanInCart.billingCycle
  } : 'NOT FOUND');
  
  // Get pricing from pricingData if available, or calculate from cart items
  const pricing = pricingData?.pricing || {
    monthlyPrice: productItems.length > 0 ? (pricingData?.pricing?.monthlyPrice || 0) : 0,
    annualTotal: productItems.length > 0 ? (pricingData?.pricing?.annualTotal || null) : null,
    setupFee: productItems.length > 0 ? (pricingData?.pricing?.setupFee || 0) : 0,
    activationFee: productItems.length > 0 ? (pricingData?.pricing?.activationFee || 0) : 0,
    todayTotal: 0,
    futureRenewal: pricingData?.pricing?.futureRenewal || { amount: 0, date: '' }
  };
  
  const accessoriesTotal = accessoryItems.reduce((sum, item) => {
    const price = item.price || 0;
    console.log('[Checkout] Adding accessory to total:', item.name, '$' + price);
    return sum + price;
  }, 0);
  const productTodayTotal = pricingData ? (pricing.todayTotal || 0) : 0;
  const { product, plan } = pricingData || { product: null, plan: null };
  const fallDetectionAddOn = state?.fallDetection && plan ? (plan.billingCycle === 'annual' ? 60 : 5) : 0;

  const hasAnnualPlan = pricingData?.plan?.billingCycle === 'annual' ||
    cartItems.some(item => item.billingCycle === 'annual');

  // Accessory-only shipping: per-item fees, use highest when multiple
  const ACCESSORY_SHIPPING: Record<string, number> = {
    'accessory-3': 5,
    'accessory-4': 10,
    'accessory-9': 10,
    'accessory-10': 5,
  };
  const accessoryShippingFee = !hasProduct && accessoryItems.length > 0
    ? (() => {
        const fees = accessoryItems
          .map(a => (a.accessoryId && ACCESSORY_SHIPPING[a.accessoryId]) || 0)
          .filter(f => f > 0);
        return fees.length > 0 ? Math.max(...fees) : 0;
      })()
    : 0;

  const shippingMethod = String(shippingInfo.shippingMethod || 'express').trim().toLowerCase();
  const planShippingFee =
    hasProduct
      ? (shippingMethod === 'overnight'
          ? 59.99
          : shippingMethod === 'overnight-saturday'
            ? 79.99
            : 0)
      : 0;
  const shippingFeeToday = hasProduct ? planShippingFee : accessoryShippingFee;
  const orderTotalToday = productTodayTotal + accessoriesTotal + fallDetectionAddOn + shippingFeeToday;
  // Always use the computed total (do not fall back in a way that can drop shipping).
  const orderTotalDisplay = orderTotalToday;

  console.log('[Checkout] Totals calculation:', {
    productTodayTotal,
    accessoriesTotal,
    fallDetectionAddOn,
    shippingMethod,
    shippingFeeToday,
    orderTotalToday,
    orderTotalDisplay,
  });

  return (
    <div className="checkout-page">
      {/* Minimal Checkout Header */}
      <header className="checkout-header">
        <div className="checkout-header-container">
          <a href="/" className="checkout-logo">
            {logoImage ? (
              <img 
                src={logoImage} 
                alt="Alert1 Logo"
              />
            ) : (
              <svg className="logo-icon" width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="18" fill="#d32f2f" stroke="#b71c1c" strokeWidth="2"/>
                <path d="M20 8L24 16H16L20 8Z" fill="white"/>
                <circle cx="20" cy="24" r="3" fill="white"/>
                <rect x="18" y="28" width="4" height="6" fill="white"/>
              </svg>
            )}
          </a>
        </div>
      </header>

      <div className="checkout-container">
        <div className="checkout-main">
        {showSystemWarning && (
          <div
            className="checkout-warning"
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeeba',
              borderRadius: '8px',
              padding: '1.25rem',
              marginBottom: '1.5rem',
              color: '#856404',
              lineHeight: 1.6
            }}
          >
            <strong>Heads up!</strong> Accessories like the Smoke Detector require an active Alert1 medical
            alert system plan. Please add a subscription plan to your cart before completing checkout. You can
            <a href="/pricing#product-tiles" style={{ marginLeft: '0.25rem', color: '#856404', fontWeight: 'bold', textDecoration: 'underline' }}>browse plans here</a>.
          </div>
        )}
          <h1 className="checkout-title">Secure, One-Step Checkout</h1>

          {/* Member Information Section */}
          <section ref={memberInfoSectionRef} className={`checkout-section ${activeSection === 'memberInfo' ? 'active' : completedSections.memberInfo ? 'completed' : 'inactive'}`}>
            <div className="section-header" onClick={() => completedSections.memberInfo && handleEditSection('memberInfo')}>
              <h2 className="section-title">
                Member Information (Person using this medical alert system)
                {completedSections.memberInfo && activeSection !== 'memberInfo' && (
                  <svg className="section-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                )}
              </h2>
            </div>
            
            {activeSection === 'memberInfo' ? (
              <div className="form-grid">
                <div className="form-field full-width">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={memberInfo.fullName}
                    onChange={(e) => setMemberInfo({...memberInfo, fullName: e.target.value})}
                  />
                </div>

                <div className="form-field full-width">
                  <input
                    type="text"
                    placeholder="Street Address"
                    value={memberInfo.streetAddress}
                    onChange={(e) => setMemberInfo({...memberInfo, streetAddress: e.target.value})}
                    maxLength={35}
                  />
                  <span className="field-note">Maximum of 35 characters</span>
                </div>

                <div className="form-field full-width">
                  <input
                    type="text"
                    placeholder="Apt/Suite/Other"
                    value={memberInfo.apt}
                    onChange={(e) => setMemberInfo({...memberInfo, apt: e.target.value})}
                    maxLength={35}
                  />
                  <span className="field-note">Maximum of 35 characters</span>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <input
                      type="text"
                      placeholder="City"
                      value={memberInfo.city}
                      onChange={(e) => setMemberInfo({...memberInfo, city: e.target.value})}
                    />
                  </div>
                  <div className="form-field">
                    <select
                      value={memberInfo.state}
                      onChange={(e) => setMemberInfo({...memberInfo, state: e.target.value})}
                    >
                      <option value="">State</option>
                      {US_STATES.map(state => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <input
                      type="text"
                      placeholder="Zip Code"
                      value={memberInfo.zipCode}
                      onChange={(e) => setMemberInfo({...memberInfo, zipCode: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-field full-width">
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={memberInfo.phoneNumber}
                    onChange={(e) => setMemberInfo({...memberInfo, phoneNumber: e.target.value})}
                  />
                </div>

                <div className="form-field full-width">
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={memberInfo.emailAddress}
                    onChange={(e) => setMemberInfo({...memberInfo, emailAddress: e.target.value})}
                  />
                  <button type="button" className="why-ask" onClick={(e) => { e.preventDefault(); alert('We use your email to send order confirmations and important updates about your medical alert service.'); }}>Why do we ask?</button>
                </div>

                <button className="save-continue-btn" onClick={handleSaveMemberInfo}>SAVE & CONTINUE</button>
              </div>
            ) : completedSections.memberInfo ? (
              <div className="section-summary">
                <p>{memberInfo.fullName}</p>
                <p>{memberInfo.streetAddress}{memberInfo.apt ? `, ${memberInfo.apt}` : ''}</p>
                <p>{memberInfo.city}, {memberInfo.state} {memberInfo.zipCode}</p>
                <p>{memberInfo.phoneNumber}    {memberInfo.emailAddress}</p>
              </div>
            ) : null}
          </section>

          {/* Protect Your Device Section */}
          <section ref={protectDeviceSectionRef} className={`checkout-section ${activeSection === 'protectDevice' ? 'active' : completedSections.protectDevice ? 'completed' : 'inactive'} ${protectionPlanInCart ? 'protection-in-cart' : ''}`}>
            <div className="section-header" onClick={() => completedSections.protectDevice && handleEditSection('protectDevice')}>
              <h2 className="section-title">
                Protect Your Device
                {protectionPlanInCart && (
                  <span style={{ 
                    fontSize: '0.85rem', 
                    color: '#28a745', 
                    marginLeft: '0.5rem',
                    fontWeight: 'normal'
                  }}>
                    (Already in Cart)
                  </span>
                )}
                {completedSections.protectDevice && activeSection !== 'protectDevice' && (
                  <svg className="section-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                )}
              </h2>
            </div>
            
            {activeSection === 'protectDevice' ? (
              <div className="protection-plan-options">
                {protectionPlanInCart ? (
                  <div style={{
                    padding: '2rem',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    border: '2px solid #ddd',
                    textAlign: 'center'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong style={{ fontSize: '1.2rem', color: '#666' }}>
                        ✓ Protection Plan Already in Cart
                      </strong>
                    </div>
                    <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                      The Added Protection Plan is already included in your cart. 
                      You can remove it below if you'd like to change your selection.
                    </p>
                    <div style={{
                      backgroundColor: '#fff',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: '1px solid #ddd'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{protectionPlanInCart.name}</strong>
                          <div style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            ${protectionPlanInCart.price?.toFixed(2)}
                            {protectionPlanInCart.billingCycle === 'annual' ? '/year' : '/month'}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveProtectionPlan}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold'
                          }}
                        >
                          Remove from Cart
                        </button>
                      </div>
                    </div>
                    <button 
                      className="save-continue-btn" 
                      onClick={handleSaveProtectionPlan}
                      style={{ marginTop: '1rem' }}
                    >
                      CONTINUE
                    </button>
                  </div>
                ) : (
                  <>
                    <div 
                      className={`protection-option ${protectionPlan === 'yes' ? 'selected' : ''}`}
                      onClick={() => handleProtectionPlanChange('yes')}
                    >
                      <input 
                        type="radio" 
                        id="protection-yes" 
                        name="protection" 
                        value="yes"
                        checked={protectionPlan === 'yes'}
                        onChange={() => {}}
                        readOnly
                      />
                      <label htmlFor="protection-yes">
                        <div className="protection-header">
                          <strong>Yes, make my life easy</strong>
                          <span className="popularity">4 out of 5 users choose this</span>
                        </div>
                        <div className="protection-badge">
                          <img src="/alert1-added-protection-badge.png" alt="Alert1 Added Protection Plan" style={{ width: '150px', marginBottom: '1rem' }} />
                        </div>
                        <div className="protection-price">
                          {pricingData?.plan?.billingCycle === 'annual' ? '$60.00/Year' : '$5.00/Month'}
                        </div>
                        <ul className="protection-features">
                          <li>FREE replacements and repairs on your medical alert system (once per year)</li>
                          <li>FREE shipping on all new orders and replacements</li>
                          <li>Full coverage for damage or destruction to device</li>
                          <li>100% peace of mind for you and your family</li>
                        </ul>
                      </label>
                    </div>

                    <div 
                      className={`protection-option ${protectionPlan === 'no' ? 'selected' : ''}`}
                      onClick={() => handleProtectionPlanChange('no')}
                    >
                      <input 
                        type="radio" 
                        id="protection-no" 
                        name="protection" 
                        value="no"
                        checked={protectionPlan === 'no'}
                        onChange={() => {}}
                        readOnly
                      />
                      <label htmlFor="protection-no">
                        <strong>No, thank you</strong>
                      </label>
                    </div>

                    <button className="save-continue-btn" onClick={handleSaveProtectionPlan}>SAVE & CONTINUE</button>
                  </>
                )}
              </div>
            ) : completedSections.protectDevice ? (
              <div className="section-summary">
                {protectionPlanInCart ? (
                  <div>
                    <p>
                      <strong>✓ Protection Plan in Cart</strong>
                    </p>
                    <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                      {protectionPlanInCart.name} - ${protectionPlanInCart.price?.toFixed(2)}
                      {protectionPlanInCart.billingCycle === 'annual' ? '/year' : '/month'}
                    </p>
                  </div>
                ) : (
                  <p>
                    <strong>{protectionPlan === 'yes' ? 'Yes, make my life easy' : 'No, thank you'}</strong>
                    {protectionPlan === 'no' && (
                      <span className="upsell-message">     Are you sure? 4 out of 5 members choose this warranty for just 28 cents a day!</span>
                    )}
                  </p>
                )}
              </div>
            ) : null}
          </section>

          {/* Shipping Information Section */}
          <section ref={shippingInfoSectionRef} className={`checkout-section ${activeSection === 'shippingInfo' ? 'active' : completedSections.shippingInfo ? 'completed' : 'inactive'}`}>
            <div className="section-header" onClick={() => completedSections.shippingInfo && handleEditSection('shippingInfo')}>
              <h2 className="section-title">
                Shipping Information
                {completedSections.shippingInfo && activeSection !== 'shippingInfo' && (
                  <svg className="section-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                )}
              </h2>
            </div>
            
            {activeSection === 'shippingInfo' ? (
              <div className="shipping-section-content">
                {/* Shipping Method Options */}
                <div className="shipping-methods">
                  {hasProduct ? (
                    <>
                      <div 
                        className={`shipping-method-option ${shippingMethod === 'express' ? 'selected' : ''}`}
                        onClick={() => setShippingInfo({...shippingInfo, shippingMethod: 'express'})}
                      >
                        <input 
                          type="radio" 
                          id="shipping-express" 
                          name="shippingMethod" 
                          value="express"
                          checked={shippingMethod === 'express'}
                          onChange={() => setShippingInfo({...shippingInfo, shippingMethod: 'express'})}
                        />
                        <label htmlFor="shipping-express">
                          <div className="shipping-method-header">
                            <strong>FedEx Express</strong>
                            <span className="shipping-price">Free</span>
                          </div>
                          <div className="shipping-delivery">{getDeliveryDate('express')}</div>
                        </label>
                      </div>

                      <div 
                        className={`shipping-method-option ${shippingMethod === 'overnight' ? 'selected' : ''}`}
                        onClick={() => setShippingInfo({...shippingInfo, shippingMethod: 'overnight'})}
                      >
                        <input 
                          type="radio" 
                          id="shipping-overnight" 
                          name="shippingMethod" 
                          value="overnight"
                          checked={shippingMethod === 'overnight'}
                          onChange={() => setShippingInfo({...shippingInfo, shippingMethod: 'overnight'})}
                        />
                        <label htmlFor="shipping-overnight">
                          <div className="shipping-method-header">
                            <strong>FedEx Overnight</strong>
                            <span className="shipping-price">$59.99</span>
                          </div>
                          <div className="shipping-delivery">{getDeliveryDate('overnight')}</div>
                        </label>
                      </div>

                      <div 
                        className={`shipping-method-option ${shippingMethod === 'overnight-saturday' ? 'selected' : ''}`}
                        onClick={() => setShippingInfo({...shippingInfo, shippingMethod: 'overnight-saturday'})}
                      >
                        <input 
                          type="radio" 
                          id="shipping-overnight-saturday" 
                          name="shippingMethod" 
                          value="overnight-saturday"
                          checked={shippingMethod === 'overnight-saturday'}
                          onChange={() => setShippingInfo({...shippingInfo, shippingMethod: 'overnight-saturday'})}
                        />
                        <label htmlFor="shipping-overnight-saturday">
                          <div className="shipping-method-header">
                            <strong>FedEx Overnight Saturday</strong>
                            <span className="shipping-price">$79.99</span>
                          </div>
                          <div className="shipping-delivery">{getDeliveryDate('overnight-saturday')}</div>
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="shipping-method-option selected">
                      <input 
                        type="radio" 
                        id="shipping-accessory-standard" 
                        name="shippingMethod" 
                        value="express"
                        checked
                        readOnly
                      />
                      <label htmlFor="shipping-accessory-standard">
                        <div className="shipping-method-header">
                          <strong>Standard Shipping</strong>
                          <span className="shipping-price">${accessoryShippingFee.toFixed(2)}</span>
                        </div>
                      </label>
                    </div>
                  )}
                </div>

                {/* Shipping Address */}
                <div className="shipping-address-section">
                  <div className="checkbox-field">
                    <input 
                      type="checkbox" 
                      id="shippingSame" 
                      checked={shippingInfo.shippingSame} 
                      onChange={(e) => setShippingInfo({...shippingInfo, shippingSame: e.target.checked})} 
                    />
                    <label htmlFor="shippingSame">Shipping address same as member info</label>
                  </div>

                  {shippingInfo.shippingSame && (
                    <div className="shipping-address-display">
                      <h4>Shipping Address</h4>
                      <p>{memberInfo.fullName}</p>
                      <p>{memberInfo.streetAddress}{memberInfo.apt ? `, ${memberInfo.apt}` : ''}</p>
                      <p>{memberInfo.city}, {memberInfo.state} {memberInfo.zipCode}</p>
                      <p>{memberInfo.phoneNumber}</p>
                    </div>
                  )}

                  {!shippingInfo.shippingSame && (
                    <div className="form-grid">
                      <div className="form-field full-width">
                        <input 
                          type="text" 
                          placeholder="Full Name"
                          value={shippingInfo.fullName}
                          onChange={(e) => setShippingInfo({...shippingInfo, fullName: e.target.value})}
                        />
                      </div>
                      <div className="form-field full-width">
                        <input 
                          type="text" 
                          placeholder="Street Address"
                          value={shippingInfo.streetAddress}
                          onChange={(e) => setShippingInfo({...shippingInfo, streetAddress: e.target.value})}
                          maxLength={35}
                        />
                      </div>
                      <div className="form-field full-width">
                        <input 
                          type="text" 
                          placeholder="Apt/Suite/Other"
                          value={shippingInfo.apt}
                          onChange={(e) => setShippingInfo({...shippingInfo, apt: e.target.value})}
                          maxLength={35}
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-field">
                          <input 
                            type="text" 
                            placeholder="City"
                            value={shippingInfo.city}
                            onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                          />
                        </div>
                        <div className="form-field">
                          <select
                            value={shippingInfo.state}
                            onChange={(e) => setShippingInfo({...shippingInfo, state: e.target.value})}
                          >
                            <option value="">State</option>
                            <option value="CA">California</option>
                            <option value="NY">New York</option>
                          </select>
                        </div>
                        <div className="form-field">
                          <input 
                            type="text" 
                            placeholder="Zip Code"
                            value={shippingInfo.zipCode}
                            onChange={(e) => setShippingInfo({...shippingInfo, zipCode: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button className="save-continue-btn" onClick={handleSaveShippingInfo}>SAVE & CONTINUE</button>
                
                <p className="shipping-disclaimer">
                  <em>Alert1 does not ship to PO boxes. Rush shipping is not available for Alaska, Hawaii, and Puerto Rico.</em>
                </p>
              </div>
            ) : completedSections.shippingInfo ? (
              <div className="section-summary">
                {hasProduct ? (
                  <>
                    <p>
                      <strong>
                        {shippingInfo.shippingMethod === 'overnight'
                          ? 'FedEx Overnight'
                          : shippingInfo.shippingMethod === 'overnight-saturday'
                            ? 'FedEx Overnight Saturday'
                            : 'FedEx Express'}
                      </strong>
                      <span className="shipping-summary-detail">
                        {'     '}{planShippingFee > 0 ? `$${planShippingFee.toFixed(2)}` : 'Free'}
                      </span>
                    </p>
                    <p className="shipping-delivery-summary">{getDeliveryDate(shippingInfo.shippingMethod === 'overnight-saturday' ? 'overnight-saturday' : shippingInfo.shippingMethod === 'overnight' ? 'overnight' : 'express')}</p>
                  </>
                ) : (
                  <p>
                    <strong>Standard Shipping</strong>
                    <span className="shipping-summary-detail">     ${accessoryShippingFee.toFixed(2)}</span>
                  </p>
                )}
              </div>
            ) : null}
          </section>

          {/* Billing Information Section */}
          <section ref={billingInfoSectionRef} className={`checkout-section ${activeSection === 'billingInfo' ? 'active' : completedSections.billingInfo ? 'completed' : 'inactive'}`}>
            <div className="section-header">
              <h2 className="section-title">Billing Information</h2>
            </div>
            
            {activeSection === 'billingInfo' && (
              <div className="billing-section-content">
                <div className="payment-method-option selected">
                  <input 
                    type="radio" 
                    id="payment-credit-card" 
                    name="paymentMethod" 
                    value="credit-card"
                    checked
                    readOnly
                  />
                  <label htmlFor="payment-credit-card">
                    <strong>Credit Card</strong>
                  </label>
                </div>

                <div className="form-grid">
                  <div className="form-field full-width">
                    <input 
                      type="text" 
                      name="cc-name"
                      autoComplete="cc-name"
                      placeholder="Name on Card"
                      value={billingInfo.nameOnCard}
                      onChange={(e) => setBillingInfo({...billingInfo, nameOnCard: e.target.value})}
                    />
                  </div>
                  
                  <div className="billing-card-row">
                    <div className="form-field card-number-field">
                      <input 
                        type="text" 
                        name="cc-number"
                        autoComplete="cc-number"
                        placeholder="Number"
                        value={billingInfo.cardNumber}
                        onChange={(e) => setBillingInfo({...billingInfo, cardNumber: e.target.value})}
                      />
                    </div>
                    <div className="form-field card-expiry-field">
                      <input 
                        type="text" 
                        name="cc-exp"
                        autoComplete="cc-exp"
                        placeholder="MM/YY"
                        value={billingInfo.expirationDate}
                        onChange={(e) => setBillingInfo({...billingInfo, expirationDate: e.target.value})}
                      />
                    </div>
                    <div className="form-field card-cvc-field">
                      <input 
                        type="text" 
                        name="cc-csc"
                        autoComplete="cc-csc"
                        placeholder={CVV_OPTIONAL ? 'CVC (optional)' : 'CVC'}
                        value={billingInfo.cvv}
                        onChange={(e) => setBillingInfo({...billingInfo, cvv: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="checkbox-field">
                    <input 
                      type="checkbox" 
                      id="billingSame" 
                      checked={billingInfo.billingSame} 
                      onChange={(e) => setBillingInfo({...billingInfo, billingSame: e.target.checked})} 
                    />
                    <label htmlFor="billingSame">Billing address same as member info</label>
                  </div>

                  {billingInfo.billingSame && (
                    <div className="billing-address-display">
                      <h4>Billing Address</h4>
                      <p>{memberInfo.fullName}</p>
                      <p>{memberInfo.streetAddress}{memberInfo.apt ? `, ${memberInfo.apt}` : ''}</p>
                      <p>{memberInfo.city}, {memberInfo.state} {memberInfo.zipCode}</p>
                      <p>{memberInfo.phoneNumber}    {memberInfo.emailAddress}</p>
                    </div>
                  )}

                  <div className="billing-agreement">
                    <h4>BY CLICKING PLACE ORDER, YOU AGREE THAT:</h4>
                    {(() => {
                      // Determine billing cycle from the selected plan
                      const billingCycle = pricingData?.plan?.billingCycle || 'monthly';
                      const isAnnual = billingCycle === 'annual';
                      
                      // Get today's payment amount (product price + any accessories)
                      // For annual: todayTotal is the annual total, for monthly: todayTotal is monthly price
                      const productPriceToday = pricing.todayTotal || (isAnnual ? (pricing.annualTotal || 0) : pricing.monthlyPrice);
                      const todayPayment = orderTotalToday > 0 ? orderTotalToday : productPriceToday;
                      
                      // Get renewal amount - use futureRenewal.amount which is already calculated correctly
                      const renewalAmount = pricing.futureRenewal?.amount || (isAnnual ? (pricing.annualTotal || pricing.monthlyPrice * 12) : pricing.monthlyPrice);
                      
                      if (isAnnual) {
                        return (
                          <>
                            <p>
                              You will pay ${todayPayment.toFixed(2)} today for your annual membership. To ensure uninterrupted service, your member will be set to continuous auto-renewal payments. 
                              Your medical alert membership will renew annually for ${renewalAmount.toFixed(2)} per year (plus applicable taxes), with your next payment due {pricing.futureRenewal.date}.
                            </p>
                            <p>
                              This means you authorize us to charge your card ${renewalAmount.toFixed(2)} annually for each renewal period. You can cancel your membership at any time by returning your device to Alert1 (via <a href="/current-members">Member Care</a>). Refunds are not offered on discounted long-term plans except as described in our refund policy (e.g., death or move to a qualifying care facility). You also agree to our{' '}
                              <a href="/terms">Terms of Service</a> and confirm that you have read and understood our{' '}
                              <a href="/privacy">Privacy Policy</a>.
                            </p>
                          </>
                        );
                      } else {
                        return (
                          <>
                            <p>
                              You will pay ${todayPayment.toFixed(2)}. To ensure uninterrupted service, your member will be set to continuous auto-renewal payments. 
                              Your medical alert membership will renew for ${renewalAmount.toFixed(2)} per month (plus applicable taxes), with your next payment due {pricing.futureRenewal.date}.
                            </p>
                            <p>
                              This means you authorize us to take ${renewalAmount.toFixed(2)} from your card for each billing period. You can cancel your membership at any time by returning your device to Alert1 (via <a href="/current-members">Member Care</a>). Refunds are not offered on discounted long-term plans except as described in our refund policy (e.g., death or move to a qualifying care facility). You also agree to our{' '}
                              <a href="/terms">Terms of Service</a> and confirm that you have read and understood our{' '}
                              <a href="/privacy">Privacy Policy</a>.
                            </p>
                          </>
                        );
                      }
                    })()}
                    
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #ddd' }}>
                      <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>Consent to Communications</h4>
                      <p style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                        By providing your contact information to Alert1 or using any Alert1 product or service, you expressly consent and agree that Alert1, its affiliates, service providers, and agents may contact you or any designated user or emergency contact by any means, including but not limited to telephone (via live call, prerecorded message, or automated dialing system), text message (SMS/MMS), email, mail, or other electronic or written communication channels, for any purpose.
                      </p>
                      <p style={{ fontSize: '0.9rem', lineHeight: '1.6', marginTop: '0.75rem' }}>
                        Such purposes may include, without limitation:
                      </p>
                      <ul style={{ fontSize: '0.9rem', lineHeight: '1.6', marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                        <li>Providing information related to system setup, service, billing, account maintenance, and support;</li>
                        <li>Collecting outstanding balances or verifying payment information;</li>
                        <li>Transmitting security, safety, or emergency alerts;</li>
                        <li>Marketing, promotional, and informational communications regarding products, services, upgrades, or offers; and</li>
                        <li>Any other business, administrative, or operational purpose.</li>
                      </ul>
                      <p style={{ fontSize: '0.9rem', lineHeight: '1.6', marginTop: '0.75rem' }}>
                        You acknowledge and agree that these communications may be made to any telephone number (including mobile or wireless numbers), email address, mailing address, or other contact method you provide to Alert1 or that Alert1 lawfully obtains. Message and data rates may apply.
                      </p>
                      <p style={{ fontSize: '0.9rem', lineHeight: '1.6', marginTop: '0.75rem' }}>
                        You may opt out of non-essential marketing communications by following the unsubscribe instructions included in such communications or by contacting Alert1 customer service. However, you understand that essential communications (including those related to safety, billing, service, or collections) are integral to the provision of Alert1 services and may not be subject to opt-out.
                      </p>
                    </div>
                  </div>
                </div>

                <button className="save-continue-btn" onClick={handleSaveBillingInfo}>SAVE & CONTINUE</button>
              </div>
            )}
          </section>

          {/* Order Total */}
          <div className="order-total-section-main">
            <h2 className="order-total-main">Order Total for Today is: <span className="total-amount-main">${orderTotalDisplay.toFixed(2)}</span></h2>
          </div>

          {TURNSTILE_SITE_KEY ? (
            <div style={{ margin: '1rem 0', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
          ) : null}

          {/* Place Order Button */}
          <button 
            ref={placeOrderBtnRef}
            className="place-order-btn" 
            disabled={!completedSections.billingInfo || isSubmitting || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
            onClick={handlePlaceOrder}
          >
            {isSubmitting ? 'PROCESSING...' : 'PLACE ORDER'}
          </button>
        </div>

        {/* Checkout Sidebar */}
        <div className="checkout-sidebar">
          <button 
            className="place-order-btn-top" 
            disabled={!completedSections.billingInfo || isSubmitting || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
            onClick={handlePlaceOrder}
          >
            {isSubmitting ? 'PROCESSING...' : 'PLACE ORDER'}
          </button>
          <p className="terms-text-small">By placing your order, you agree to Alert1's Privacy Policy and Terms of Use.</p>

          {/* Order Summary Box */}
          <div className="order-summary-box">
            <div className="summary-header">
              <h3>Order Summary</h3>
              <a href="/cart" className="edit-cart">Edit Cart</a>
            </div>

            {/* Display all cart items */}
            {cartItems.length === 0 ? (
              <div className="order-item">
                <div className="item-details">
                  <div className="item-name" style={{ fontStyle: 'italic', color: '#666' }}>
                    {loading ? 'Loading cart...' : 'No items in cart'}
                  </div>
                </div>
              </div>
            ) : (
              cartItems.map((item, index) => {
                console.log('[Checkout] Rendering cart item:', item);
                return (
                  <div key={item.id} className="order-item" style={{ position: 'relative' }}>
                    {/* X button in top-right corner */}
                    <button
                      onClick={() => {
                        cartStorage.removeItemById(item.id);
                        const updatedItems = cartStorage.getItems();
                        setCartItems(updatedItems);
                        logDiagnostic('info', `Removed ${item.name} from cart`);
                      }}
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        width: '24px',
                        height: '24px',
                        padding: '0',
                        backgroundColor: 'transparent',
                        color: '#999',
                        border: 'none',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        lineHeight: '1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        zIndex: 10,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                        e.currentTarget.style.color = '#dc3545';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#999';
                      }}
                      aria-label="Remove item"
                      title="Remove item"
                    >
                      ×
                    </button>
                    
                    <div className="item-image">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} style={{ width: '60px', height: '60px', objectFit: 'contain' }} />
                      ) : (
                        <div className="placeholder-image"></div>
                      )}
                    </div>
                    <div className="item-details">
                      <div className="item-name">{item.name}</div>
                      {item.type === 'product' && item.planName && (
                        <div className="item-plan">{item.planName}</div>
                      )}
                      {item.type === 'accessory' && item.billingCycle && (
                        <div className="item-plan">
                          {item.billingCycle === 'annual' ? 'Annual Plan' : 'Monthly Plan'}
                        </div>
                      )}
                      {isLifeBeaconProduct(item) && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>
                          <span style={{ fontWeight: 600 }}>Wear as: </span>
                          <label style={{ marginRight: '0.75rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name={`checkout-deviceStyle-${item.id}`}
                              checked={item.deviceStyle === 'pendant'}
                              onChange={() => {
                                const updated = { ...item, deviceStyle: 'pendant' as const };
                                cartStorage.addItem(updated);
                                setCartItems(cartStorage.getItems());
                              }}
                            />
                            {' '}Pendant
                          </label>
                          <label style={{ cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name={`checkout-deviceStyle-${item.id}`}
                              checked={item.deviceStyle === 'watch'}
                              onChange={() => {
                                const updated = { ...item, deviceStyle: 'watch' as const };
                                cartStorage.addItem(updated);
                                setCartItems(cartStorage.getItems());
                              }}
                            />
                            {' '}Watch
                          </label>
                        </div>
                      )}
                      <div className="item-qty">Qty: 1</div>
                    </div>
                    <div className="item-price">
                      {item.type === 'accessory' && item.price ? (
                        <>
                          ${item.price.toFixed(2)}
                          {item.name.toLowerCase().includes('protection plan') 
                            ? '/month' 
                            : (item.billingCycle === 'annual' ? '/year' : item.billingCycle === 'one-time' ? '' : '/month')}
                        </>
                      ) : item.type === 'product' && pricingData ? (
                        <>${pricing.todayTotal?.toFixed(2)}</>
                      ) : (
                        <>$0.00</>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Fall Detection Add-On if applicable */}
            {state?.fallDetection && plan && (
              <div className="order-item">
                <div className="item-image">
                  <div className="placeholder-icon">🚨</div>
                </div>
                <div className="item-details">
                  <div className="item-name">Fall Detection Add-On</div>
                  <div className="item-qty">Qty: 1</div>
                </div>
                <div className="item-price">
                  {plan.billingCycle === 'annual' ? '$60.00' : '$5.00/mo'}
                </div>
              </div>
            )}

            <div className="order-item">
              <div className="item-image">
                <div className="placeholder-icon">📡</div>
              </div>
              <div className="item-details">
                <div className="item-name">No Activation Fee</div>
                <div className="item-qty">Qty: 1</div>
              </div>
              <div className="item-price">$0.00</div>
            </div>

            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal:</span>
                <span>${(productTodayTotal + accessoriesTotal)?.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Shipping:</span>
                <span>${shippingFeeToday.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Tax:</span>
                <span>$0.00</span>
              </div>
              <div className="total-row final-total">
                <span>Order Total for Today is:</span>
                <span>${orderTotalDisplay.toFixed(2)}</span>
              </div>
            </div>

            {hasAnnualPlan && (
            <div className="annual-plan-disclosure" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f9f9f9', fontSize: '0.875rem' }}>
              Our annual discounts are offered on the basis of a long term commitment. Users on annual plans can cancel any time, but will not receive a refund unless the user has passed away or moved into a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed.
            </div>
          )}

            <div className="referral-program">
              <p>Participate in Alert1's <strong>Friends for Life Referral Program</strong> and receive one FREE month of service for yourself and your friend if they sign up for a new Alert1 membership. No limit.</p>
            </div>
          </div>

          {/* Promo Code Box */}
          <div className="promo-code-box">
            <h3>🎁 Have a Promo Code?</h3>
            <div className="promo-input-group">
              <input 
                type="text" 
                placeholder="Enter code" 
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
              <button className="apply-btn">Apply</button>
            </div>
          </div>

          {/* Future Billing Reminder */}
          <div className="billing-reminder-box">
            <h3>📅 Future Billing Reminder</h3>
            {plan && pricing?.futureRenewal && (
              <div className="billing-details">
                <div className="billing-row">
                  <strong>Recurring {plan.billingCycle === 'annual' ? 'Annual' : 'Monthly'} Plan</strong>
                  <strong>${pricing.futureRenewal.amount.toFixed(2)}</strong>
                </div>
                <div className="billing-note">(plus applicable taxes)</div>
                <div className="billing-renewal">Automatically renews on {pricing.futureRenewal.date}.</div>
              </div>
            )}
          </div>

          {/* Shop with Confidence Box */}
          <div className="confidence-box">
            <h3>🔒 Shop with Confidence</h3>
            <p>Your orders are safe and secure.</p>
            <ul className="confidence-list">
              <li>24/7 Monitoring</li>
              <li>No Long-Term Contracts</li>
              <li>Cancel any time by returning equipment</li>
            </ul>
            <div className="google-reviews">
              <div className="stars">★★★★★</div>
              <div className="rating">4.6 / 5</div>
              <div className="review-source">Google Customer Reviews</div>
            </div>
            <div className="confidence-bbb">
              <BBBSeal variant="rating" />
            </div>
          </div>
        </div>
      </div>

      {/* Diagnostics Panel (Development/Testing) */}
      {(process.env.NODE_ENV === 'development' || showDiagnostics) && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '400px',
          maxHeight: '500px',
          backgroundColor: '#1a1a1a',
          color: '#fff',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 9999,
          fontSize: '0.85rem',
          overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>🔍 Checkout Diagnostics</h3>
            <button
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              style={{
                background: 'transparent',
                border: '1px solid #666',
                color: '#fff',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              {showDiagnostics ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {diagnostics.errors.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#ff6b6b' }}>Errors ({diagnostics.errors.length}):</strong>
              <div style={{ maxHeight: '100px', overflow: 'auto', marginTop: '0.5rem' }}>
                {diagnostics.errors.slice(-5).map((error, idx) => (
                  <div key={idx} style={{ color: '#ff6b6b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    {error}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {diagnostics.warnings.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <strong style={{ color: '#ffd93d' }}>Warnings ({diagnostics.warnings.length}):</strong>
              <div style={{ maxHeight: '100px', overflow: 'auto', marginTop: '0.5rem' }}>
                {diagnostics.warnings.slice(-5).map((warning, idx) => (
                  <div key={idx} style={{ color: '#ffd93d', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                    {warning}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div>
            <strong style={{ color: '#6bcf7f' }}>Info ({diagnostics.info.length}):</strong>
            <div style={{ maxHeight: '150px', overflow: 'auto', marginTop: '0.5rem' }}>
              {diagnostics.info.slice(-10).map((info, idx) => (
                <div key={idx} style={{ color: '#6bcf7f', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                  {info}
                </div>
              ))}
            </div>
          </div>
          
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #333' }}>
            <div style={{ fontSize: '0.75rem', color: '#999' }}>
              <div>Cart Items: {cartItems.length}</div>
              <div>API URL: {API_URL}</div>
              <div>Pricing Data: {pricingData ? 'Loaded' : 'Missing'}</div>
              <div>Completed Sections: {Object.values(completedSections).filter(Boolean).length}/4</div>
            </div>
          </div>
        </div>
      )}

      {isSubmitting && (
        <div className="checkout-processing-overlay">
          <div className="checkout-processing-dialog">
            <h2>Processing Your Order</h2>
            <p>
              We&rsquo;re confirming your payment and securing your Alert1 device. This usually takes less than a minute.
              Please do not close or refresh this page.
            </p>
            <div className="checkout-processing-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CheckoutPage;
